/**
 * Minimal browser-side API client for /api/portal/*.
 *
 * Reads the `portal_csrf` cookie (HttpOnly=false per PORTAL_DEPLOY_SSOT
 * §8.1) and echoes it back via the `X-CSRF-Token` header on unsafe
 * methods, matching the server's double-submit guard.
 */

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

export class PortalApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`${status} ${code}`);
    this.name = 'PortalApiError';
  }
}

function readCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'DELETE' | 'PATCH' | 'PUT';
  body?: unknown;
  signal?: AbortSignal;
}

export async function portalApi<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (!SAFE_METHODS.has(method)) {
    const csrf = readCookie('portal_csrf');
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }
  const res = await fetch(path, {
    method,
    headers,
    credentials: 'include',
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let parsed: unknown = undefined;
  try {
    parsed = text ? JSON.parse(text) : undefined;
  } catch {
    // upstream returned non-JSON; surface as opaque error.
  }
  if (!res.ok) {
    const code =
      (parsed as { error?: string } | undefined)?.error ?? `PORTAL.UPSTREAM_${res.status}`;
    throw new PortalApiError(res.status, code);
  }
  return parsed as T;
}
